import { FC, useRef } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { BadgeBanner } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { useBillingRebrandVisibility } from '../../hooks/use-billing-rebrand-visibility';
import { BILLING_REBRAND_INFO_FF } from './constants';

interface BillingRebrandPromoProps {
  containerStyles?: Interpolation<Theme>;
  source?: string;
}

const DEFAULT_COMPONENT_ID = 'billing-rebrand-promo';

export const BillingRebrandPromo: FC<BillingRebrandPromoProps> = ({ containerStyles, source }) => {
  const { t } = useTranslation('organizationBilling');
  const trackingId = `${DEFAULT_COMPONENT_ID}:${source}`;

  const pendoEventTrackingRef = useRef(false);

  const { showBanner, ref, markAsSeen } = useBillingRebrandVisibility({
    componentId: DEFAULT_COMPONENT_ID,
    source,
    featureFlag: BILLING_REBRAND_INFO_FF,
  });

  if (!showBanner) {
    return null;
  }

  if (pendoEventTrackingRef.current === false) {
    pendo.track(`${trackingId}:viewed`);
    pendoEventTrackingRef.current = true;
  }

  return (
    <div ref={ref} css={[styles.container, containerStyles]} data-trackingId={trackingId}>
      <BadgeBanner styles={styles.banner}>
        <div css={styles.header}>
          <BadgeBanner.AnimatedBadge label={t('New')} />
          <Text size='medium' weight='bold'>
            {t('Invoices & Billing in Settings')}
          </Text>
        </div>
        <div css={styles.body}>
          <Text size='medium'>
            {t('“Billing” is now “Invoices & Billing” and can be found in Organization Settings.')}
          </Text>
          <Text size='medium'>
            {t('Here you can find past and upcoming invoices and manage your billing details.')}
          </Text>
        </div>
        <Button
          variant='tertiary'
          css={styles.actionButton}
          onClick={markAsSeen}
          trackingId={`${trackingId}:okay-button`}
        >
          {t('Okay')}
        </Button>
        <div css={styles.cardArrowBox} />
      </BadgeBanner>
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    z-index: ${theme.zIndex.modal};
    @media (max-width: ${breakpoints.medium.min}px) {
      display: none;
    }
  `,
  banner: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing(1)};
    width: 336px;
    position: relative;
  `,
  header: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  actionButton: css`
    display: flex;
    align-self: flex-end;
  `,
  cardArrowBox: css`
    position: absolute;
    width: 24px;
    height: 24px;
    transform: rotate(45deg);
    background-color: ${theme.colors.secondary.seaweed5};
    border-left: 1px solid ${theme.colors.secondary.seaweed50};
    border-top: 1px solid ${theme.colors.secondary.seaweed50};
    top: -13px; // half width + 1px for border
    right: 24px;
  `,
};
